import each from 'lodash/each';
import merge from 'lodash/merge';
import get from 'lodash/get';
import { authenticationReady, componentLoader, configurationReady, mdsCssReady, mdsIconsReady, loadFiles, manifestReady, installTrackingAgent, setCustomAttributesForNewRelic } from 'loader-base';
import configurationReadyQs from './configuration-qs';
import ecReady from './initialize-ec';
import { initializeLazyBundleLoader, initializeLazyLoader, lazyDownloadComponent, registerPaths } from './lazy-loader';
import { bundlesReady, handleClientSpecificBundle } from './bundle-loader';
import setDefaultValuesEc from './default-values-ec';
import { getAppContext } from '../../loader-base/src/helpers/app-context-helper';

let lazyDownloading = false;
let lazyBundleDownloading = false;
let savedLoaderConfig;
let manifest = {};
const contextInfo = {};

/**
 * Add client custom stylesheets
 * @param {*} css
 */
function loadCustomStyles(css) {
    return loadFiles(css.customStyles)
        .then(() => Promise.resolve());
}

function checkAndInitializeMorningstarUsageTracking(loaderConfig) {
    if (loaderConfig.morningstarUsageTracking && loaderConfig.morningstarUsageTracking.enabled) {
        installTrackingAgent(loaderConfig.morningstarUsageTracking.applicationId, () => {
            setCustomAttributesForNewRelic(getAppContext(loaderConfig));
        });

        try {
            loaderConfig.usageTracking = loaderConfig.usageTracking || [];
            loaderConfig.usageTracking.push({
                provider: 'newRelic',
            });
        } catch (e) {
            console.error(e);
        }
        if (loaderConfig.usageTracking.length >= 2) {
            console.warn('Usage tracking is enabled for 2 or more providers. This can degrade the performance of the webpage');
        }
    }
}

function load(loaderConfig = {}) {
    setDefaultValuesEc(loaderConfig);
    if (loaderConfig.lazy && (loaderConfig.lazy.lazyDownloadComponent || loaderConfig.lazy.lazyLoadComponent || loaderConfig.lazy.bundlesToDownload)) {
        loaderConfig.lazy.lazyLoadComponent = true;
        loaderConfig.angular.bootstrap = false;
    }

    contextInfo.runtime = merge({}, loaderConfig.runtime);
    contextInfo.apiTokens = merge({}, loaderConfig.apiTokens);
    contextInfo.instid = loaderConfig.componentSource.instid;

    let promiseChain = Promise.resolve();

    // NewRelic agent needs to be installed in <head> section
    // this should be done in the sync code and not wait for download of config or bundle
    checkAndInitializeMorningstarUsageTracking(loaderConfig);

    // Get configuration...
    switch (loaderConfig.configuration.source.type) {
        case 'service': {
            promiseChain = promiseChain.then(() => authenticationReady(loaderConfig.apiTokens))
                .then((newApiTokens) => {
                    loaderConfig.apiTokens = merge(loaderConfig.apiTokens, newApiTokens);
                    return configurationReady(loaderConfig.configuration, loaderConfig.apiTokens.apiGatewayToken);
                });
            break;
        }
        case 'qs':
        case 'mwc': {
            promiseChain = promiseChain.then(() => configurationReadyQs(loaderConfig.configuration));
            break;
        }
        case 'none':
        default: {
            promiseChain = promiseChain.then(() => configurationReady(loaderConfig.configuration, loaderConfig.apiTokens.apiGatewayToken));
            break;
        }
    }
    // ...and set configuration
    promiseChain = promiseChain.then((config) => {
        loaderConfig.config = config;
        return true;
    });

    // Download MDS icons/style if set in config
    promiseChain = promiseChain.then(() => mdsIconsReady(loaderConfig.css.mds, loaderConfig.componentSource.environment))
        .then(() => mdsCssReady(loaderConfig.css.mds, loaderConfig.componentSource.environment));

    switch (loaderConfig.componentSource.type) {
        case 'resolver': {
            const prefixes = ['angular', 'asterix-', 'jquery', 'vue', 'underscore'];

            const asterixSetupFilter = function asterixSetupFilter(file) {
                let match = false;
                for (let i = 0; i < prefixes.length; i++) {
                    if (file.indexOf(prefixes[i]) > -1) {
                        match = true;
                        break;
                    }
                }
                return match;
            };
            const antiAsterixSetupFilter = function antiAsterixSetupFilter(file) {
                return !asterixSetupFilter(file);
            };
            promiseChain = promiseChain.then(() => manifestReady(loaderConfig.componentSource, loaderConfig.config.components))
                .then((manifestContent) => {
                    manifest = manifestContent;
                    if (loaderConfig.componentSource.type === 'resolver' && loaderConfig.componentSource.exclusions) {
                        manifest.resolutions = manifest.resolutions.filter(resolution => !loaderConfig.componentSource.exclusions.includes(resolution.name));
                    }
                    const asterixMwcIndex = manifest.resolutions.findIndex(resolution => resolution.name === 'asterix-mwc');
                    if (asterixMwcIndex > 0) {
                        const asterixCoreIndex = manifest.resolutions.findIndex(resolution => resolution.name === 'asterix-core');
                        const asterixMwcResolution = manifest.resolutions.splice(asterixMwcIndex, 1)[0];
                        manifest.resolutions.splice(asterixCoreIndex, 0, asterixMwcResolution);
                    }
                    return componentLoader.componentInitializationReady(manifest.componentDependencies, manifest.resolutions);
                });

            if (loaderConfig.lazy.lazyDownloadComponent) {
                lazyDownloading = true;
                promiseChain = promiseChain.then(() => initializeLazyLoader(manifest.componentDependencies, manifest.resolutions, loaderConfig.config))
                    .then(() => componentLoader.componentsReady(manifest.resolutions, loaderConfig.css, asterixSetupFilter))
                    .then(() => componentLoader.mwcReady(loaderConfig))
                    .then(() => ecReady(manifest.resolutions, loaderConfig.config, loaderConfig));
            } else {
                promiseChain = promiseChain.then(() =>
                    componentLoader.componentsReady(manifest.resolutions, loaderConfig.css, asterixSetupFilter))
                    .then(() => componentLoader.componentsReady(manifest.resolutions, loaderConfig.css, antiAsterixSetupFilter))
                    .then(() => componentLoader.mwcReady(loaderConfig))
                    .then(() => ecReady(manifest.resolutions, loaderConfig.config, loaderConfig));
            }
            break;
        }
        case 'qs':
        case 'mwc':
        default: {
            promiseChain = promiseChain
                .then(() => handleClientSpecificBundle(loaderConfig))
                .then(() => bundlesReady(loaderConfig, componentLoader.getLoadedFiles()))
                .then((manifestContent) => {
                    manifest = manifestContent;
                    let returnPromise = Promise.resolve();
                    if (loaderConfig.additionalComponents && loaderConfig.additionalComponents.length) {
                        const components = {};
                        let mainBundleName;
                        loaderConfig.additionalComponents.forEach((element) => {
                            if (typeof element === 'string') {
                                components[element] = {
                                    type: element,
                                };
                            } else {
                                components[element.componentName] = {
                                    type: element.componentName,
                                    version: element.componentVersion,
                                };
                                ({ mainBundleName } = element);
                            }
                        });
                        loaderConfig.componentSource.type = 'resolver';
                        const bundleInfo = window.morningstar.asterix ? window.morningstar.asterix.meta.get(mainBundleName, 'bundleInfo') || {} : {};
                        bundleInfo.dependencies = bundleInfo.dependencies || {};
                        loaderConfig.componentSource.overrides = merge({}, bundleInfo.dependencies, loaderConfig.componentSource.overrides);
                        Object.keys(loaderConfig.componentSource.overrides).forEach((overrideKey) => {
                            const versionFirstChar = loaderConfig.componentSource.overrides[overrideKey].charAt(0);
                            if (versionFirstChar.toLowerCase() === 'v') {
                                loaderConfig.componentSource.overrides[overrideKey] = loaderConfig.componentSource.overrides[overrideKey].substring(1);
                            }
                        });
                        if (loaderConfig.componentSource.overrides['angular-mocks']) {
                            delete loaderConfig.componentSource.overrides['angular-mocks'];
                        }
                        if (loaderConfig.componentSource.overrides['asterix-ng-dev']) {
                            delete loaderConfig.componentSource.overrides['asterix-ng-dev'];
                        }
                        loaderConfig.ignore = loaderConfig.ignore.concat(Object.keys(bundleInfo.dependencies));
                        returnPromise = returnPromise.then(() => manifestReady(loaderConfig.componentSource, components).then((result) => {
                            Object.keys(result.componentDependencies).forEach((key) => {
                                const dependencyList = result.componentDependencies[key];
                                dependencyList.splice(0, dependencyList.length, ...dependencyList.filter(dep => !loaderConfig.ignore.includes(dep)));
                            });
                            if (loaderConfig.lazy.lazyDownloadAdditionalComponent) {
                                lazyDownloading = true;
                                return initializeLazyLoader(result.componentDependencies, result.resolutions, loaderConfig.config);
                            }
                            return componentLoader.componentsReady(result.resolutions.filter(dep => !loaderConfig.ignore.includes(dep.name)), loaderConfig.css);
                        }));
                    }
                    returnPromise = returnPromise.then(() => componentLoader.mwcReady(loaderConfig)).then(() => ecReady(manifest.resolutions, loaderConfig.config, loaderConfig));
                    return returnPromise;
                });
            if (loaderConfig.lazy.bundlesToDownload) {
                lazyBundleDownloading = true;
                promiseChain = promiseChain.then(() => initializeLazyBundleLoader(loaderConfig.config));
            }
            break;
        }
    }

    promiseChain = promiseChain.then(() => {
        savedLoaderConfig = loaderConfig;
        return loadCustomStyles(loaderConfig.css);
    });
    // For backwards compatible reasons
    manifest.loaderConfig = loaderConfig;
    promiseChain = promiseChain.then(() => Promise.resolve(manifest));
    return promiseChain;
}

function reset() {
    componentLoader.resetInternal();
}

function createAndReturnTag(componentTagName, componentId, containerId) {
    // Create component tag...
    const componentTag = document.createElement(componentTagName);
    componentTag.setAttribute('mstar-component-id', componentId);
    // ... and attach it to the container.
    const container = document.createElement('div');
    container.setAttribute('id', containerId);
    container.appendChild(componentTag);
    return container;
}

function registerAndResetAsterix(componentId) {
    // Register default config for component types that need it.
    each(window.morningstar.components._defaultConfig, (defConfig, typeName) => {
        if (!window.morningstar.asterix.config.getDefaultConfig(typeName)) {
            window.morningstar.asterix.config.addType(typeName, defConfig);
        }
    });
    // Register component paths in Asterix.
    registerPaths(componentId);
    // Handle Asterix reset/init.
    const path = window.morningstar.asterix.instanceRegistry.find(componentId).modulePath;
    window.morningstar.asterix.instanceRegistry.init(path);
}

function loadMarketsComponent(containerParent, containerId, componentTagName, componentId) {
    const container = document.createElement('div');
    container.setAttribute('id', containerId);
    container.style.height = '100%';
    containerParent.appendChild(container);

    const marketsComponentSettings = get(savedLoaderConfig, `config.components[${componentId}].settings`, {});

    return new Promise((resolve) => {
        const instance = window.morningstar.components[componentTagName].createComponent({
            element: container,
            initialConfiguration: {
                component: {
                    configuration: marketsComponentSettings,
                },
            },
        });
        resolve(instance);
    });
}

function setBundleCssDisabled(componentName, status) {
    let href = '';
    const bundleName = `${componentName}-bundle.css`.toLowerCase();
    for (let i = 0; i < document.styleSheets.length; i += 1) {
        if (document.styleSheets[i].href != null) {
            href = document.styleSheets[i].href.split('/');
            href = href[href.length - 1];
            if (href.toLowerCase() === bundleName) {
                document.styleSheets[i].disabled = status;
                break;
            }
        }
    }
}

function lazyLoad(containerParent, containerId, componentTagName, componentId, mwcName) {
    const initMarkets = componentTagName.indexOf('markets-components-') === 0 && savedLoaderConfig.lazy.initializeMarketsComponent;
    let promiseChain;

    if (!containerParent || !containerId) {
        if (lazyDownloading) {
            return lazyDownloadComponent(componentTagName, componentLoader.getLoadedFiles());
        } else if (lazyBundleDownloading) {
            return new Promise((resolve) => {
                const clonedLoaderConfig = merge({}, savedLoaderConfig);
                clonedLoaderConfig.lazy.bundlesToDownload = {};
                clonedLoaderConfig.lazy.bundlesToDownload[componentTagName] = true;
                bundlesReady(clonedLoaderConfig, componentLoader.getLoadedFiles()).then(() => {
                    resolve();
                });
            });
        }
        return Promise.resolve();
    }

    if (savedLoaderConfig.css.toggleCssWithComponent && componentTagName) {
        setBundleCssDisabled(componentTagName, false);
    }

    // Lazy downloading
    if (window.morningstar.asterix && lazyDownloading && (componentTagName.indexOf('markets-') === 0 || componentTagName.indexOf('sal-') === 0)) {
        window.morningstar.asterix.config.setOption('dynamic', true);
        promiseChain = lazyDownloadComponent(componentTagName, componentLoader.getLoadedFiles()).then(() => {
            // Handle Asterix reset/init.
            const path = window.morningstar.asterix.instanceRegistry.find(componentId).modulePath;
            window.morningstar.asterix.instanceRegistry.init(path);
            return window.morningstar.asterix.instanceRegistry.find(componentId);
        });
        if (initMarkets) {
            promiseChain.then(() => loadMarketsComponent(containerParent, containerId, componentTagName, componentId));
        }
        return promiseChain;
    } else if (lazyDownloading && componentTagName.indexOf('ec-') === 0) {
        return new Promise((resolve) => {
            lazyDownloadComponent(componentTagName, componentLoader.getLoadedFiles()).then(() => {
                window.morningstar.asterix.config.setOption('dynamic', true);
                const container = createAndReturnTag(componentTagName, componentId, containerId, containerParent);
                registerAndResetAsterix(componentId);
                // Bootstrap the element
                angular.bootstrap(container, window.morningstar.asterix.ng.getAngularModuleDependencies());
                containerParent.appendChild(container);
                resolve(window.morningstar.asterix.instanceRegistry.find(componentId));
            });
        });
    } else if (lazyDownloading) {
        if (window.morningstar.asterix) {
            window.morningstar.asterix.config.setOption('dynamic', true);
        }
        return new Promise((resolve) => {
            lazyDownloadComponent(mwcName || componentTagName, componentLoader.getLoadedFiles()).then(() => {
                const instance = document.createElement(componentTagName);
                instance.setAttribute('mwc-id', componentId);
                containerParent.appendChild(instance);
                resolve(instance);
            });
        });
    } else if (lazyBundleDownloading) {
        promiseChain = new Promise((resolve) => {
            const clonedLoaderConfig = merge({}, savedLoaderConfig);
            clonedLoaderConfig.lazy.bundlesToDownload = {};
            clonedLoaderConfig.lazy.bundlesToDownload[componentTagName] = true;
            bundlesReady(clonedLoaderConfig, componentLoader.getLoadedFiles()).then(() => {
                if (window.morningstar.asterix) {
                    window.morningstar.asterix.config.setOption('dynamic', true);
                    const container = createAndReturnTag(componentTagName, componentId, containerId, containerParent);
                    registerAndResetAsterix(componentId);
                    // Bootstrap the element
                    angular.bootstrap(container, window.morningstar.asterix.ng.getAngularModuleDependencies());
                    containerParent.appendChild(container);
                    resolve(window.morningstar.asterix.instanceRegistry.find(componentId));
                } else { // Assume MWC
                    const instance = document.createElement(componentTagName);
                    instance.setAttribute('mwc-id', componentId);
                    containerParent.appendChild(instance);
                    resolve(instance);
                }
            });
        });
        if (initMarkets) {
            promiseChain.then(() => loadMarketsComponent(containerParent, containerId, componentTagName, componentId));
        }
        return promiseChain;
    }

    // Lazy loading
    if (componentTagName.indexOf('ec-') === 0) {
        return new Promise((resolve) => {
            const path = window.morningstar.asterix.instanceRegistry.find(componentId).modulePath;
            window.morningstar.asterix.instanceRegistry.init(path);
            const container = createAndReturnTag(componentTagName, componentId, containerId, containerParent);
            // Bootstrap the element
            angular.bootstrap(container, window.morningstar.asterix.ng.getAngularModuleDependencies());
            containerParent.appendChild(container);
            resolve(window.morningstar.asterix.instanceRegistry.find(componentId));
        });
    } else if (initMarkets) {
        // Handle Asterix reset/init. Might not be needed for markets?
        // const path = morningstar.asterix.instanceRegistry.find(componentId).modulePath;
        // morningstar.asterix.instanceRegistry.init(path);
        return loadMarketsComponent(containerParent, containerId, componentTagName, componentId);
    } else if (!(componentTagName.indexOf('markets-') === 0 || componentTagName.indexOf('sal-') === 0)) {
        return new Promise((resolve) => {
            const instance = document.createElement(componentTagName);
            instance.setAttribute('mwc-id', componentId);
            containerParent.appendChild(instance);
            resolve(instance);
        });
    }
    return promiseChain;
}

function lazyRemove(containerId) {
    const container = document.getElementById(containerId);
    if (container && container.remove) {
        container.remove();
        console.log(`Removed container with id: ${containerId}`);
    }
    // Because IE
    if (container && container.parentNode) {
        container.parentNode.removeChild(container);
        console.log(`Removed container with id: ${containerId} (IE style)`);
    }

    if (savedLoaderConfig.css.toggleCssWithComponent && container && container.firstChild) {
        setBundleCssDisabled(container.firstChild.nodeName, true);
    }
}

function getRuntimeEnvironment() {
    return contextInfo.runtime.environment;
}

function getApiToken(tokenType) {
    if (tokenType.indexOf('Token') >= 0) {
        return contextInfo.apiTokens[tokenType];
    }
    return contextInfo.apiTokens[`${tokenType}Token`];
}

function getInstId() {
    return contextInfo.instid;
}

export default {
    context: {
        getRuntimeEnvironment,
        getApiToken,
        getInstId,
    },
    lazyLoad,
    lazyRemove,
    load,
    reset,
};



// WEBPACK FOOTER //
// ./src/loader.js